import { Box, styled } from '@mui/material';
import IconCaretLeft from 'assets/img/icons/caret-left.svg?react';
import IconCaretRight from 'assets/img/icons/caret-right.svg?react';
import { useTranslation } from 'react-i18next';
import { LwButton, LwIconButton } from 'redesign';
import { ReactNode } from 'react';

type Props = {
  label: string | null;
  centerLabel?: ReactNode;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  todayPosition?: 'left' | 'right';
};

type DateControlTodayButtonProps = {
  onToday: () => void;
  label: string;
};

const DateControlTodayButton = ({ onToday, label }: DateControlTodayButtonProps) => (
  <LwButton onClick={onToday} color="secondary">
    {label}
  </LwButton>
);

export const DateControlBase = ({
  label,
  centerLabel,
  onPrev,
  onNext,
  onToday,
  todayPosition,
}: Props) => {
  const { t } = useTranslation('web-common');
  const resolvedTodayPosition = todayPosition || 'left';
  const todayButtonLabel = t('date-control.actions.today-button');

  return (
    <StyledContainer>
      {resolvedTodayPosition === 'left' ? (
        <DateControlTodayButton onToday={onToday} label={todayButtonLabel} />
      ) : null}
      <StyledDateControlBox>
        <Box display="flex" flexDirection="row" alignItems="center">
          <LwIconButton
            size="medium"
            iconColorMode="stroke"
            onClick={onPrev}
            color="secondary"
            aria-label={t('date-control.navigation.previous-period-aria-label')}
          >
            <StyledSvgIcon as={IconCaretLeft} />
          </LwIconButton>

          {centerLabel && <StyledInfoBox>{centerLabel}</StyledInfoBox>}

          <LwIconButton
            size="medium"
            iconColorMode="stroke"
            onClick={onNext}
            color="secondary"
            aria-label={t('date-control.navigation.next-period-aria-label')}
          >
            <StyledSvgIcon as={IconCaretRight} />
          </LwIconButton>
        </Box>

        {label ? <StyledLabelBox>{label}</StyledLabelBox> : null}
      </StyledDateControlBox>
      {resolvedTodayPosition === 'right' ? (
        <DateControlTodayButton onToday={onToday} label={todayButtonLabel} />
      ) : null}
    </StyledContainer>
  );
};

const StyledContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  flexShrink: 0,
  gap: theme.spacing(4),
}));

const StyledSvgIcon = styled('svg')(({ theme }) => ({
  height: theme.spacing(4),
  width: theme.spacing(4),
}));

const StyledDateControlBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  margin: 'auto',
  alignItems: 'center',
  gap: theme.spacing(8),
}));

const StyledLabelBox = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.lwBlack[100],
  display: 'flex',
  flexShrink: 0,
  fontWeight: theme.typography.fontWeightMedium,
  whiteSpace: 'nowrap',
  width: '160px',
  ...theme.typography.body2,
}));

const StyledInfoBox = styled(Box)(({ theme }) => ({
  margin: '0 1rem',
  color: theme.palette.lwPrimaryAccentDark['100'],
  fontSize: '1rem',
  fontStyle: 'normal',
  fontWeight: '500',
  lineHeight: 'normal',
  width: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));
